import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Calendar, Users, DollarSign, Ship, AlertTriangle, Building2, TrendingDown, Package, TrendingUp } from 'lucide-react';
import { useLanguage, LanguageSwitcher } from '@/components/LanguageContext';

const Article3 = () => {
  const { t, language } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-white">
      <div className="container mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/">
            <Button variant="outline" className="mb-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('common.backToHome')}
            </Button>
          </Link>
          
          <div className="flex items-center mb-4">
            <Badge className="bg-red-600 text-white mr-4">
              <Users className="h-3 w-3 mr-1" />
              Labor
            </Badge>
            <span className="text-sm text-gray-600">Kuehne+Nagel News / Reuters</span>
          </div>
          
          <div className="flex items-center text-sm text-muted-foreground mb-6">
            <Calendar className="h-4 w-4 mr-1" />
            February 2026
            <span className="mx-2">•</span>
            <span>5 min read</span>
          </div>
          
          <div className="flex justify-end mb-4">
            <LanguageSwitcher />
          </div>
        </div>

        {/* Article Content */}
        <article className="max-w-4xl mx-auto">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            {t('article.3.title')}
          </h1>
          
          <img 
            src="https://cdn-trans.info/uploads/2025/08/e6e083b7021abb861810bbec88f.webp" 
            alt="Cross-border E-commerce Logistics"
            className="w-full h-64 object-cover rounded-lg mb-8"
          />

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-700 mb-6 font-medium">
              {t('article.3.intro')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section1.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.p1')}
            </p>

            <p className="mb-6">
              {t('article.3.p2')}
            </p>

            <div className="bg-red-50 p-6 rounded-lg my-8 border border-red-100">
              <h3 className="font-bold text-red-900 mb-2 flex items-center">
                <DollarSign className="mr-2" />
                {t('article.3.highlight.title')}
              </h3>
              <p className="text-sm text-red-800">
                {t('article.3.highlight.content')}
              </p>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section2.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.p3')}
            </p>

            <p className="mb-6">
              {t('article.3.p4')}
            </p>

            <div className="bg-orange-50 p-6 rounded-lg my-8">
              <h3 className="font-bold text-orange-900 mb-4">{language === 'ko' ? '시장 규모 및 성장 전망' : 'Market Size & Growth Projections'}</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-orange-800">
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <TrendingUp className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '국내 전자상거래 물류' : 'Domestic E-commerce Logistics'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '2022년:' : '2022:'}</strong> {language === 'ko' ? '€336.7억' : '€336.7bn'}</li>
                    <li><strong>{language === 'ko' ? '2025년:' : '2025:'}</strong> {language === 'ko' ? '€486.0억' : '€486.0bn'}</li>
                    <li><strong>{language === 'ko' ? '2026년 예측:' : '2026 Forecast:'}</strong> {language === 'ko' ? '€516.2억' : '€516.2bn'}</li>
                    <li><strong>{language === 'ko' ? '2030년 예측:' : '2030 Forecast:'}</strong> {language === 'ko' ? '€653.4억' : '€653.4bn'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 flex items-center">
                    <Package className="mr-1 h-4 w-4" />
                    {language === 'ko' ? '국경간 전자상거래 물류' : 'Cross-Border E-commerce Logistics'}
                  </h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '2022년:' : '2022:'}</strong> {language === 'ko' ? '€63.3억' : '€63.3bn'}</li>
                    <li><strong>{language === 'ko' ? '2025년:' : '2025:'}</strong> {language === 'ko' ? '€96.1억' : '€96.1bn'}</li>
                    <li><strong>{language === 'ko' ? '2026년 예측:' : '2026 Forecast:'}</strong> {language === 'ko' ? '€101.8억' : '€101.8bn'}</li>
                    <li><strong>{language === 'ko' ? '2030년 예측:' : '2030 Forecast:'}</strong> {language === 'ko' ? '€129.4억' : '€129.4bn'}</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section3.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.p5')}
            </p>

            <p className="mb-6">
              {t('article.3.p6')}
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">{t('article.3.section4.title')}</h2>
            
            <p className="mb-6">
              {t('article.3.p7')}
            </p>

            <p className="mb-6">
              {t('article.3.p8')}
            </p>

            <div className="bg-purple-50 p-6 rounded-lg mt-8 border border-purple-100">
              <h3 className="font-bold text-purple-900 mb-2 flex items-center">
                <Package className="mr-2" />
                {language === 'ko' ? '규제 영향 분석' : 'Regulatory Impact Analysis'}
              </h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm text-purple-800">
                <div>
                  <h4 className="font-semibold mb-2">{language === 'ko' ? '시장 구성' : 'Market Composition'}</h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '국내 배송:' : 'Domestic Delivery:'}</strong> {language === 'ko' ? '84-85% 시장 점유율' : '84-85% market share'}</li>
                    <li><strong>{language === 'ko' ? '국경간 물류:' : 'Cross-border:'}</strong> {language === 'ko' ? '15-16% 점유율' : '15-16% share'}</li>
                    <li><strong>{language === 'ko' ? '2025년 가치:' : '2025 Value:'}</strong> {language === 'ko' ? '전 세계 €96.1억' : '€96.1bn Global'}</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2">{language === 'ko' ? '2026년 중반 변화' : 'Mid-2026 Changes'}</h4>
                  <ul className="space-y-1">
                    <li><strong>{language === 'ko' ? '관세 면제:' : 'Duty Exemptions:'}</strong> {language === 'ko' ? '기존 면제 제거' : 'Existing exemptions removed'}</li>
                    <li><strong>{language === 'ko' ? '규정 준수:' : 'Compliance:'}</strong> {language === 'ko' ? '새로운 요구사항 도입' : 'New requirements introduced'}</li>
                    <li><strong>{language === 'ko' ? '비용 증가:' : 'Cost Increase:'}</strong> {language === 'ko' ? '국경간 운영업체 비용 상승' : 'Higher costs for cross-border operators'}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Source Attribution */}
          <div className="mt-12 p-6 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600 mb-2">
              {t('common.sourceAttribution')} Trans.info - Cross-Border E-commerce Logistics Faces Regulatory Shift.
            </p>
            <p className="text-sm text-gray-600 mb-4">
              {t('common.sourceDetails')}
            </p>
            <a 
              href="https://trans.info/en/cross-border-logistics-457757" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block"
            >
              <Button className="bg-red-600 hover:bg-red-700 text-white">
                {t('common.readOriginalArticle')}
              </Button>
            </a>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <Link to="/">
              <Button variant="outline">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('common.backToAllArticles')}
              </Button>
            </Link>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Article3;